// Types
interface FieldOption {
  value: string;
  label: string;
}

interface MeterField {
  name: string;
  label: string;
  placeholder?: string;
  type: string;
  required: boolean;
  readonly?: boolean;
  options?: FieldOption[];
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
}

interface MeterFieldsConfig {
  common: MeterField[];
  electricity: MeterField[];
  water: MeterField[];
  gas: MeterField[];
}

interface MeterTypeIds {
  [key: string]: string;
}

export const METER_FIELDS_CONFIG: MeterFieldsConfig = {
  common: [
    // {
    //   name: "meterId",
    //   label: "Meter ID",
    //   placeholder: "Enter meter ID",
    //   type: "text",
    //   required: true,
    //   readonly: true,
    // },
    {
      name: "meterType",
      label: "Meter Type",
      placeholder: "Select meter type",
      type: "select",
      required: true,
      options: [
        { value: "Electricity", label: "Electricity" },
        { value: "Water", label: "Water" },
        { value: "Gas", label: "Gas" },
      ],
    },
    {
      name: "siteId",
      label: "Site ID",
      placeholder: "Enter site ID",
      type: "text",
      required: true,
    },
    {
      name: "locationId",
      label: "Location ID",
      placeholder: "Enter location ID",
      type: "text",
      required: false,
    },
    {
      name: "serialNumber",
      label: "Serial Number",
      placeholder: "Enter serial number",
      type: "text",
      required: true,
    },
    {
      name: "manufacturer",
      label: "Manufacturer",
      placeholder: "Meter manufacturer name",
      type: "text",
      required: false,
      // options: [
      //   { value: "SENSUS", label: "SENSUS" },
      //   { value: "ITRON", label: "ITRON" },
      //   { value: "LANDIS_GYR", label: "Landis+Gyr" },
      //   { value: "HONEYWELL", label: "Honeywell" },
      //   { value: "KAMSTRUP", label: "Kamstrup" },
      // ],
    },
    {
      name: "model",
      label: "Model",
      placeholder: "Enter model number",
      type: "text",
      required: false,
    },
    {
      name: "firmwareVersion",
      label: "Firmware Version",
      placeholder: "Enter firmware version",
      type: "text",
      required: false,
    },
    {
      name: "installDate",
      label: "Install Date",
      placeholder: "Select installation date",
      type: "date",
      required: false,
    },
    {
      name: "status",
      label: "Status",
      placeholder: "Select status",
      type: "select",
      required: false,
      options: [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "maintenance", label: "Maintenance" },
      ],
    },
    {
      name: "communicationType",
      label: "Communication Type",
      placeholder: "Select communication type",
      type: "select",
      required: false,
      options: [
        { value: "RF", label: "RF" },
        { value: "CELLULAR", label: "Cellular" },
        { value: "PLC", label: "PLC" },
        { value: "ETHERNET", label: "Ethernet" },
        { value: "WIFI", label: "WiFi" },
      ],
    },
    {
      name: "communicationModuleId",
      label: "Communication Module ID",
      placeholder: "Enter communication module ID",
      type: "text",
      required: false,
    },
    {
      name: "readingFrequency",
      label: "Reading Frequency",
      placeholder: "Select reading frequency",
      type: "select",
      required: false,
      options: [
        { value: "15min", label: "15 Minutes" },
        { value: "30min", label: "30 Minutes" },
        { value: "hourly", label: "Hourly" },
        { value: "daily", label: "Daily" },
      ],
    },
    {
      name: "timezone",
      label: "Timezone",
      placeholder: "Select timezone",
      type: "select",
      required: false,
      options: [
        { value: "UTC", label: "UTC (Universal Time)" },
        { value: "EST", label: "Eastern Standard Time" },
        { value: "PST", label: "Pacific Standard Time" },
        { value: "CST", label: "Central Standard Time" },
        { value: "IST", label: "Indian Standard Time" },
      ],
    },
    {
      name: "encryption",
      label: "Encryption Enabled",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
    {
      name: "meteringMode",
      label: "Metering Mode",
      placeholder: "Select metering mode",
      type: "select",
      required: false,
      options: [
        { value: "prepaid", label: "Prepaid" },
        { value: "postpaid", label: "Postpaid" },
      ],
    },
    {
      name: "lat",
      label: "Latitude",
      placeholder: "Enter latitude",
      type: "number",
      required: false,
    },
    {
      name: "lng",
      label: "Longitude",
      placeholder: "Enter longitude",
      type: "number",
      required: false,
    },
    // {
    //   name: "assignToCustomer",
    //   label: "Assign to Consumer",
    //   placeholder: "Select a consumer",
    //   type: "select",
    //   required: false,
    //   options: [], // Will be populated from API
    // },
    {
      name: "useAsLoadProfile",
      label: "Use As Load Profile",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
  ],

  electricity: [
    {
      name: "phaseType",
      label: "Phase Type",
      placeholder: "Select phase type",
      type: "select",
      required: false,
      options: [
        { value: "single", label: "Single Phase" },
        { value: "three", label: "Three Phase" },
      ],
    },
    {
      name: "voltageRating",
      label: "Voltage Rating",
      placeholder: "Enter voltage rating (e.g., 230V)",
      type: "text",
      required: false,
    },
    {
      name: "currentRating",
      label: "Current Rating",
      placeholder: "Enter current rating",
      type: "text",
      required: false,
    },
    {
      name: "ctRatio",
      label: "CT Ratio",
      placeholder: "Enter current transformer ratio",
      type: "text",
      required: false,
    },
    {
      name: "ptRatio",
      label: "PT Ratio",
      placeholder: "Enter potential transformer ratio",
      type: "text",
      required: false,
    },
    {
      name: "maxDemand",
      label: "Max Demand",
      placeholder: "Enter maximum demand",
      type: "number",
      required: false,
    },
    {
      name: "tariffCode",
      label: "Tariff Code",
      placeholder: "Enter tariff code",
      type: "text",
      required: false,
    },
    {
      name: "loadProfileType",
      label: "Load Profile Type",
      placeholder: "Select load profile type",
      type: "select",
      required: false,
      options: [
        { value: "TOU", label: "Time of Use (TOU)" },
        { value: "INTERVAL", label: "Interval Data" },
        { value: "DEMAND", label: "Demand Profile" },
      ],
    },
    {
      name: "supportsNetMetering",
      label: "Supports Net Metering",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
    {
      name: "reactiveEnergySupport",
      label: "Reactive Energy Support",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
  ],

  water: [
    {
      name: "meterSize",
      label: "Meter Size",
      placeholder: "Enter meter size (e.g., 15mm, 50mm)",
      type: "text",
      required: false,
    },
    {
      name: "flowRange",
      label: "Flow Range",
      placeholder: "Enter flow range (min-max)",
      type: "text",
      required: false,
    },
    {
      name: "unitOfMeasure",
      label: "Unit of Measure",
      placeholder: "Select unit of measure",
      type: "select",
      required: false,
      options: [
        { value: "cubic_meters", label: "Cubic Meters (m³)" },
        { value: "liters", label: "Liters" },
        { value: "gallons", label: "Gallons" },
      ],
    },
    {
      name: "pressureRating",
      label: "Pressure Rating",
      placeholder: "Enter pressure rating",
      type: "text",
      required: false,
    },
    {
      name: "temperatureRange",
      label: "Temperature Range",
      placeholder: "Enter temperature range",
      type: "text",
      required: false,
    },
    {
      name: "leakDetectionEnabled",
      label: "Leak Detection Enabled",
      placeholder: "",
      type: "Checkbox",
      required: false,
    },
    {
      name: "reverseFlowDetection",
      label: "Reverse Flow Detection",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
    {
      name: "tamperDetection",
      label: "Tamper Detection",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
  ],

  gas: [
    {
      name: "meterCapacity",
      label: "Meter Capacity",
      placeholder: "Enter capacity (m³/hr)",
      type: "number",
      required: false,
    },
    {
      name: "pressureRating",
      label: "Pressure Rating",
      placeholder: "Enter operating pressure",
      type: "text",
      required: false,
    },
    {
      name: "temperatureCompensation",
      label: "Temperature Compensation",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
    {
      name: "gasType",
      label: "Gas Type",
      placeholder: "Select gas type",
      type: "select",
      required: false,
      options: [
        { value: "natural_gas", label: "Natural Gas" },
        { value: "LPG", label: "LPG" },
        { value: "propane", label: "Propane" },
        { value: "butane", label: "Butane" },
      ],
    },
    {
      name: "unitOfMeasure",
      label: "Unit of Measure",
      placeholder: "Select unit of measure",
      type: "select",
      required: false,
      options: [
        { value: "cubic_meters", label: "Cubic Meters (m³)" },
        { value: "therms", label: "Therms" },
        { value: "ccf", label: "CCF (Hundred Cubic Feet)" },
      ],
    },
    {
      name: "valveStatus",
      label: "Valve Status",
      placeholder: "Select valve status",
      type: "select",
      required: false,
      options: [
        { value: "open", label: "Open" },
        { value: "closed", label: "Closed" },
        { value: "unknown", label: "Unknown" },
      ],
    },
    {
      name: "cutOffCapability",
      label: "Cut-off Capability",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
    {
      name: "flameDetection",
      label: "Flame Detection",
      placeholder: "",
      type: "checkbox",
      required: false,
    },
  ],
};

// Meter type IDs for fetching specific data
export const METER_TYPE_IDS: MeterTypeIds = {
  gas: "9a3948b0-42b3-11f0-a9a0-9fd706385992",
  water: "5a567b20-42bb-11f0-a9a0-9fd706385992",
  electricity: "1394fec0-42b9-11f0-a9a0-9fd706385992",
};

// Default fields configuration for Meters.js page
export const DEFAULT_METER_FIELDS: MeterField[] = [
  {
    name: "manufacturer",
    label: "Manufacturer",
    placeholder: "Select Manufacturer",
    type: "select",
    required: true,
    options: [
      { value: "SENSUS", label: "SENSUS" },
      { value: "ITRON", label: "ITRON" },
    ],
  },
  {
    name: "meterType",
    label: "Meter Type",
    placeholder: "Select Meter Type",
    type: "select",
    required: true,
    options: [], // Will be populated from meterTypeOptions
  },
  {
    name: "meterSerialNo",
    label: "Meter Serial No",
    placeholder: "Enter Serial Number",
    type: "text",
    required: false,
  },
  {
    name: "meterBadgeNo",
    label: "Meter Badge No",
    placeholder: "Enter Badge Number",
    type: "text",
    required: false,
  },
  {
    name: "meterInstallDate",
    label: "Meter Install Date",
    placeholder: "dd/mm/yyyy",
    type: "date",
    required: false,
  },
  {
    name: "status",
    label: "Status",
    placeholder: "Enter Status",
    type: "text",
    required: false,
  },
  // {
  //   name: "useAsLoadProfile",
  //   label: "Use As Load Profile",
  //   type: "checkbox",
  //   required: false,
  // },
  {
    name: "assignToCustomer",
    label: "Assign To Customer",
    placeholder: "Select Customer",
    type: "select",
    required: false,
    options: [], // Will be populated from customers
  },
  {
    name: "siteId",
    label: "Site Id",
    placeholder: "Enter Site ID",
    type: "text",
    required: true,
  },
  // {
  //   name: "existingMeterNumber",
  //   label: "Existing Meter Number",
  //   placeholder: "Enter Existing Meter Number",
  //   type: "text",
  //   required: false,
  // },
  {
    name: "lineLtCtRatio",
    label: "Line Lt Ct Ratio",
    placeholder: "Enter Line Lt Ct Ratio",
    type: "text",
    required: false,
  },
  {
    name: "meterBoxDimensions",
    label: "Meter Box Dimensions",
    placeholder: "Enter Meter Box Dimensions",
    type: "text",
    required: false,
  },
  {
    name: "meterLegalStatus",
    label: "Meter Legal Status",
    placeholder: "Enter Meter Legal Status",
    type: "text",
    required: false,
  },
  {
    name: "remarks",
    label: "Remarks",
    placeholder: "Enter Remarks",
    type: "text",
    required: false,
    fullWidth: true, // This field takes full width
  },
];

// Helper function to get fields based on meter type
export const getFieldsForMeterType = (meterType: string): MeterField[] => {
  if (!meterType) return METER_FIELDS_CONFIG.common;

  const commonFields = METER_FIELDS_CONFIG.common;
  const typeSpecificFields =
    METER_FIELDS_CONFIG[meterType.toLowerCase() as keyof MeterFieldsConfig] ||
    [];

  return [...commonFields, ...typeSpecificFields];
};

// Helper function to get only type-specific fields
export const getTypeSpecificFields = (meterType: string): MeterField[] => {
  if (!meterType) return [];
  return (
    METER_FIELDS_CONFIG[meterType.toLowerCase() as keyof MeterFieldsConfig] ||
    []
  );
};
